import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../TimerSummaryModal';
import Dialog from '../ui/Dialog';
import DateInput from '../ui/DateInput';
import EntityPicker from '../ui/EntityPicker';
import TimeTextInput from '../ui/TimeTextInput';
import {
  REPOP_CHOICES, WEEKDAY_KEYS, emptyReminderForm, formFromRow, reminderFormError, reminderPayload,
  type ReminderForm, type ReminderRow, type RepeatKind,
} from '../../utils/reminderModel';

export const REMINDERS_QUERY_KEY = ['reminders', 'mine'] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  /** The signed-in employee; the default recipient. */
  selfId: string;
  /** The people the server allows (always includes the caller). One person = no picker. */
  recipients: Array<{ id: string; name: string; self: boolean }>;
  /** Editing an existing reminder (creator only); absent = a new one. */
  editing?: ReminderRow | null;
}

/**
 * W-01 - "התראה חדשה": message, day + time, who (managers / team leads only get a picker; the server
 * enforces the list), repeat once / daily / weekly (+ end date), "pop again every X minutes", and
 * "keep until I mark done". Dates and times are Israeli local and are sent as typed.
 */
export default function ReminderFormDialog({ open, onClose, selfId, recipients, editing }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const uid = useId();
  const fid = (k: string) => `${uid}-${k}`;
  const [form, setForm] = useState<ReminderForm>(() => emptyReminderForm(selfId));
  const [problem, setProblem] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  // Opening (or switching to another reminder) starts from the right values.
  useEffect(() => {
    if (!open) return;
    setForm(editing ? formFromRow(editing) : emptyReminderForm(selfId));
    setProblem(null);
    setServerError(null);
  }, [open, editing, selfId]);

  const set = (patch: Partial<ReminderForm>) => { setForm((f) => ({ ...f, ...patch })); setProblem(null); setServerError(null); };

  const save = useMutation({
    mutationFn: () => {
      const body = reminderPayload(form, selfId);
      return editing ? api.updateReminder(editing.id, body) : api.createReminder(body);
    },
    onSuccess: () => {
      toast('success', t(editing ? 'reminder.savedEdit' : 'reminder.saved'));
      void queryClient.invalidateQueries({ queryKey: REMINDERS_QUERY_KEY });
      onClose();
    },
    onError: (err) => setServerError(apiErrorMessage(err) || t('reminder.saveFailed')),
  });

  const submit = () => {
    const e = reminderFormError(form);
    if (e) { setProblem(e); return; }
    save.mutate();
  };

  const toggleDay = (d: number) =>
    set({ weekdays: form.weekdays.includes(d) ? form.weekdays.filter((x) => x !== d) : [...form.weekdays, d] });

  const err = (key: string) => (problem === key ? <p role="alert" className="error-text mt-1">{t(`reminder.error.${key}`)}</p> : null);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(editing ? 'reminder.editTitle' : 'reminder.newTitle')}
      size="md"
      footer={(
        <div className="flex w-full justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={save.isPending}>{t('reminder.cancel')}</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={save.isPending} aria-busy={save.isPending || undefined}>
            {save.isPending && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
            {t('reminder.save')}
          </button>
        </div>
      )}
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submit(); }} data-reminder-form="">
        <div>
          <label htmlFor={fid('message')} className="label">{t('reminder.message')}</label>
          <textarea
            id={fid('message')}
            value={form.message}
            maxLength={500}
            rows={3}
            onChange={(e) => set({ message: e.target.value })}
            placeholder={t('reminder.messagePlaceholder')}
            aria-invalid={problem === 'message' || undefined}
            className="input"
          />
          {err('message')}
        </div>

        {recipients.length > 1 && (
          <div>
            <label htmlFor={fid('who')} className="label">{t('reminder.forWhom')}</label>
            {/* DQ-449 (D-203/D-207): a name picker with search. */}
            <EntityPicker
              id={fid('who')}
              options={recipients.map((r) => ({ value: r.id, label: r.self ? t('reminder.me') : r.name }))}
              value={form.userId}
              clearable={false}
              onChange={(v) => set({ userId: v })}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor={fid('date')} className="label">{t('reminder.date')}</label>
            <DateInput id={fid('date')} value={form.date} onChange={(e) => set({ date: e.target.value })} className="input" />
            {err('date')}
          </div>
          <div>
            <label htmlFor={fid('time')} className="label">{t('reminder.time')}</label>
            <TimeTextInput id={fid('time')} value={form.time} onChange={(v) => set({ time: v })} className="input tabular-nums" />
            {err('time')}
          </div>
        </div>

        <div>
          <label htmlFor={fid('repeat')} className="label">{t('reminder.repeat')}</label>
          <select id={fid('repeat')} value={form.repeat} onChange={(e) => set({ repeat: e.target.value as RepeatKind })} className="input">
            <option value="once">{t('reminder.repeatOnce')}</option>
            <option value="daily">{t('reminder.repeatDaily')}</option>
            <option value="weekly">{t('reminder.repeatWeekly')}</option>
          </select>
        </div>

        {form.repeat === 'weekly' && (
          <fieldset>
            <legend className="label">{t('reminder.weekdays')}</legend>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAY_KEYS.map((k, d) => {
                const on = form.weekdays.includes(d);
                return (
                  <button
                    key={k}
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    onClick={() => toggleDay(d)}
                    className={`btn btn-sm ${on ? 'btn-primary' : 'btn-secondary'}`}
                    data-weekday={d}
                  >
                    {t(`reminder.weekday.${k}`)}
                  </button>
                );
              })}
            </div>
            {err('weekdays')}
          </fieldset>
        )}

        {form.repeat !== 'once' && (
          <div>
            <label htmlFor={fid('until')} className="label">{t('reminder.until')}</label>
            <DateInput id={fid('until')} value={form.until} min={form.date} onChange={(e) => set({ until: e.target.value })} className="input" />
            <p className="mt-1 text-xs text-text-2">{t('reminder.untilHint')}</p>
            {err('until')}
          </div>
        )}

        <div>
          <label htmlFor={fid('repop')} className="label">{t('reminder.repop')}</label>
          <select
            id={fid('repop')}
            value={form.repopMinutes === null ? '' : String(form.repopMinutes)}
            onChange={(e) => {
              const v = e.target.value === '' ? null : Number(e.target.value);
              set({ repopMinutes: v, keepUntilDone: v === null ? false : form.keepUntilDone });
            }}
            className="input"
          >
            <option value="">{t('reminder.repopNone')}</option>
            {REPOP_CHOICES.map((m) => <option key={m} value={m}>{t('reminder.repopEvery', { count: m })}</option>)}
          </select>
          <p className="mt-1 text-xs text-text-2">{t('reminder.repopHint')}</p>
        </div>

        <div>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.keepUntilDone}
              disabled={form.repopMinutes === null}
              onChange={(e) => set({ keepUntilDone: e.target.checked })}
              className="mt-0.5"
            />
            <span>
              {t('reminder.keep')}
              <span className="block text-xs text-text-2">{t('reminder.keepHint')}</span>
            </span>
          </label>
          {err('keep')}
        </div>

        {serverError && <p role="alert" className="error-text">{serverError}</p>}
      </form>
    </Dialog>
  );
}
